import { getAuthRequestHeaders } from "@qw/auth";
import { isGeneratedDraftResourceLink } from "@qw/qw-common";
import {
  Button,
  downloadFile,
  type ExtractedResult,
  FileViewer,
  getFileIcon,
  Icon,
  type PdfViewerFile,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import type { ToolUIPart } from "ai";
import { type FC, useMemo, useState } from "react";

import { getResourceLinkHandler, getResourceLinkTitle, isResourceLink } from "../chat.helpers";

import type { FileResourceLinkHandler, McpResourceLink } from "../chat.types";
import { useResourceLinkFirstSeen } from "../hooks/use-resource-link-first-seen";
import { useChat } from "../providers";
import { GeneratedEmailDraftCard } from "./generated-email-draft-card";

export interface ChatSessionMessageResourceLinksProps {
  toolInvocationOutput: ToolUIPart["output"];
  chatSessionId?: string;
}

export const ChatSessionMessageResourceLinks: FC<ChatSessionMessageResourceLinksProps> = ({
  toolInvocationOutput,
  chatSessionId,
}) => {
  const { fileResourceLinkHandler, resourceLinkHandlers } = useChat();

  const structuredContent = useMemo(
    () => (toolInvocationOutput as any)?.structuredContent ?? {},
    [toolInvocationOutput]
  );
  const extractedResults = useMemo(() => structuredContent?.result?.data?.extractedResults ?? [], [structuredContent]);

  // Extract MCP-native resource_link items
  const resourceLinks = useMemo(
    () => (((toolInvocationOutput as any)?.content ?? []) as unknown[]).filter(isResourceLink),
    [toolInvocationOutput]
  );

  const generatedDraftLinks = useMemo(
    () =>
      resourceLinkHandlers?.draft ? resourceLinks.filter((r: McpResourceLink) => isGeneratedDraftResourceLink(r)) : [],
    [resourceLinks, resourceLinkHandlers]
  );

  const remainingResourceLinks = useMemo(() => {
    if (generatedDraftLinks.length === 0) {
      return resourceLinks;
    }
    const generatedUris = new Set(generatedDraftLinks.map((r: McpResourceLink) => r.uri));
    return resourceLinks.filter((r: McpResourceLink) => !generatedUris.has(r.uri));
  }, [resourceLinks, generatedDraftLinks]);

  // Collect all PDF links that point to qw.file-upload
  const pdfFiles = useMemo(() => {
    return remainingResourceLinks
      .filter(
        (r: any) =>
          typeof r.uri === "string" &&
          r.uri.startsWith("qw.file-upload.") &&
          ((r.mimeType ?? "").includes("pdf") || (r.title ?? "").toLowerCase().endsWith(".pdf"))
      )
      .map((r: any) => ({
        id: r.uri.replace("qw.file-upload.", "") as string,
        filename: (r.title ?? "Document.pdf") as string,
        mimeType: (r.mimeType ?? "application/pdf") as string,
        resourceLink: r as McpResourceLink,
      }));
  }, [remainingResourceLinks]);

  // Collect all Excel links that point to qw.file-upload
  // TODO: This is a little messy. We should clean it up.
  const excelFiles = useMemo(() => {
    return remainingResourceLinks
      .filter(
        (r: any) =>
          typeof r.uri === "string" &&
          r.uri.startsWith("qw.file-upload.") &&
          ((r.mimeType ?? "").includes("spreadsheet") ||
            (r.mimeType ?? "").includes("excel") ||
            (r.title ?? "").toLowerCase().endsWith(".xlsx") ||
            (r.title ?? "").toLowerCase().endsWith(".xls"))
      )
      .map((r: any) => ({
        id: r.uri.replace("qw.file-upload.", "") as string,
        filename: (r.title ?? "Spreadsheet.xlsx") as string,
        mimeType: (r.mimeType ?? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") as string,
        resourceLink: r as McpResourceLink,
      }));
  }, [remainingResourceLinks]);

  // Collect all Word document links that point to qw.file-upload
  const docxFiles = useMemo(() => {
    return remainingResourceLinks
      .filter(
        (r: any) =>
          typeof r.uri === "string" &&
          r.uri.startsWith("qw.file-upload.") &&
          ((r.mimeType ?? "").includes("wordprocessing") || (r.title ?? "").toLowerCase().endsWith(".docx"))
      )
      .map((r: any) => ({
        id: r.uri.replace("qw.file-upload.", "") as string,
        filename: (r.title ?? "Document.docx") as string,
        mimeType: (r.mimeType ?? "application/vnd.openxmlformats-officedocument.wordprocessingml.document") as string,
        resourceLink: r as McpResourceLink,
      }));
  }, [remainingResourceLinks]);

  const otherResourceLinks = useMemo(
    () =>
      remainingResourceLinks.filter(
        (r: any) => r?.uri && !r.uri.startsWith("qw.file-upload.") && getResourceLinkHandler(r, resourceLinkHandlers)
      ),
    [remainingResourceLinks, resourceLinkHandlers]
  );

  if (
    !pdfFiles?.length &&
    !excelFiles?.length &&
    !docxFiles?.length &&
    !otherResourceLinks?.length &&
    !generatedDraftLinks.length
  ) {
    return null;
  }

  return (
    <>
      {generatedDraftLinks.length > 0 ? (
        <div className="-mx-2 my-1 flex flex-col gap-2 text-foreground">
          {generatedDraftLinks.map((resource: McpResourceLink) => (
            <GeneratedEmailDraftCard key={resource.uri} resource={resource} />
          ))}
        </div>
      ) : null}

      {pdfFiles?.length || excelFiles?.length || docxFiles?.length || otherResourceLinks?.length ? (
        <div className="flex flex-col gap-2 my-1 py-1 pl-3 border-l-3 border-border">
          <PdfFileChatSessionMessageResourceLinks
            pdfFiles={pdfFiles}
            extractedResults={extractedResults}
            fileResourceLinkHandler={fileResourceLinkHandler}
            chatSessionId={chatSessionId}
          />

          <DownloadableFileChatSessionMessageResourceLinks
            files={excelFiles}
            fileResourceLinkHandler={fileResourceLinkHandler}
            fallbackLabel="Spreadsheet"
            chatSessionId={chatSessionId}
          />

          <DownloadableFileChatSessionMessageResourceLinks
            files={docxFiles}
            fileResourceLinkHandler={fileResourceLinkHandler}
            fallbackLabel="Document"
            chatSessionId={chatSessionId}
          />

          {otherResourceLinks.length > 0 && <OtherChatSessionMessageResourceLinks resourceLinks={otherResourceLinks} />}
        </div>
      ) : null}
    </>
  );
};

interface FileResourceLink {
  id: string;
  filename: string;
  mimeType: string;
  resourceLink: McpResourceLink;
}

const runFileResourceLinkAction = async ({
  chatSessionId,
  defaultAction,
  fileResourceLinkHandler,
  resourceLink,
}: {
  chatSessionId?: string;
  defaultAction: () => void | Promise<void>;
  fileResourceLinkHandler?: FileResourceLinkHandler;
  resourceLink: McpResourceLink;
}) => {
  if (!fileResourceLinkHandler) {
    await defaultAction();
    return;
  }

  await fileResourceLinkHandler.onClick(resourceLink, {
    chatSessionId,
    defaultAction,
  });
};

const PdfFileChatSessionMessageResourceLinks: FC<{
  pdfFiles: Array<PdfViewerFile & { resourceLink: McpResourceLink }>;
  extractedResults: ExtractedResult[];
  fileResourceLinkHandler?: FileResourceLinkHandler;
  chatSessionId?: string;
}> = ({ pdfFiles, extractedResults, fileResourceLinkHandler, chatSessionId }) => {
  const [open, setOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<PdfViewerFile>();

  const headers = getAuthRequestHeaders();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {pdfFiles.length > 0 &&
        pdfFiles.map((file: any, index: number) => (
          <Button
            key={file.id}
            size="sm"
            variant="link"
            onClick={() =>
              void runFileResourceLinkAction({
                chatSessionId,
                defaultAction: () => {
                  setSelectedPdf(file);
                  setOpen(true);
                },
                fileResourceLinkHandler,
                resourceLink: file.resourceLink,
              })
            }
            className="max-w-full justify-start text-muted-foreground hover:text-primary font-normal gap-2"
          >
            <Icon icon="description" className="mx-0! text-xs!" />
            <span className="truncate">{file.filename || `PDF ${index + 1}`}</span>
          </Button>
        ))}

      <SheetContent className="sm:w-3/4 lg:w-3/5 sm:max-w-full">
        <SheetHeader>
          <SheetTitle>PDF Viewer</SheetTitle>
        </SheetHeader>

        <FileViewer
          extractedResults={extractedResults}
          files={pdfFiles}
          headers={headers}
          selectedFile={selectedPdf}
          setSelectedFile={setSelectedPdf}
        />
      </SheetContent>
    </Sheet>
  );
};

/**
 * Generic component for rendering downloadable file links (Excel, Word, etc.)
 * Used for file types that don't have a custom viewer and just need download functionality.
 */
const DownloadableFileChatSessionMessageResourceLinks: FC<{
  files: FileResourceLink[];
  fileResourceLinkHandler?: FileResourceLinkHandler;
  fallbackLabel: string;
  chatSessionId?: string;
}> = ({ files, fileResourceLinkHandler, fallbackLabel, chatSessionId }) => {
  if (!files?.length) {
    return null;
  }

  return (
    <>
      {files.map((file, index) => (
        <Button
          key={file.id}
          size="sm"
          variant="link"
          onClick={() =>
            void runFileResourceLinkAction({
              chatSessionId,
              defaultAction: () => downloadFile(file),
              fileResourceLinkHandler,
              resourceLink: file.resourceLink,
            })
          }
          className="max-w-full justify-start text-muted-foreground hover:text-primary font-normal gap-2"
        >
          <Icon icon={getFileIcon(file.mimeType)} className="mx-0! text-xs!" />
          <span className="truncate">{file.filename || `${fallbackLabel} ${index + 1}`}</span>
        </Button>
      ))}
    </>
  );
};

export const OtherChatSessionMessageResourceLinks: FC<{ resourceLinks: any[] }> = ({ resourceLinks }) => {
  if (!resourceLinks?.length) {
    return null;
  }

  return (
    <>
      {resourceLinks.map((resource: any) => (
        <ChatSessionMessageResourceLinkButton key={resource.uri} resource={resource} />
      ))}
    </>
  );
};

export const ChatSessionMessageResourceLinkButton: FC<{ resource: any }> = ({ resource }) => {
  const resourceLinkHandler = useResourceLinkFirstSeen(resource);

  const title = useMemo(() => getResourceLinkTitle(resource), [resource]);

  const tooltip = useMemo(
    () => resourceLinkHandler?.tooltip?.(resource) || resource.description,
    [resourceLinkHandler, resource]
  );

  if (!resourceLinkHandler) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="sm"
          variant="link"
          onClick={() => resourceLinkHandler.onClick(resource)}
          className="max-w-full justify-start text-muted-foreground hover:text-primary font-normal gap-2"
        >
          <Icon icon={resourceLinkHandler.icon ?? "open_in_new"} className="mx-0! text-xs!" />
          <span className="truncate">{title}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
    </Tooltip>
  );
};
